import { ArrowLeft, BookOpen, Check, ExternalLink, Lock, MessageSquare, ShieldCheck, X } from "lucide-react";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatDate } from "../api";
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import Markdown from "../components/Markdown";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "../components/Ui";
import { NoClient } from "../components/Workflow";
import { useApi } from "../hooks";
import { calmHeadings, splitCover } from "../lesson-doc";
import { plainTextToMarkdown } from "../markdown-text";

type Procedure = {
  id: number;
  title: string;
  description: string | null;
  estimated_minutes: number;
  kb_references: string[] | string | null;
  step_count: number;
};
type Run = {
  id: number;
  checklist_id: number;
  checklist_title: string;
  client_id: number | null;
  client_name: string | null;
  exec_name: string | null;
  status: "in_progress" | "completed" | "abandoned";
  started_at: string;
  completed_at: string | null;
  step_count: number;
  done_count: number;
};
type Step = {
  item_id: number;
  step_number: number;
  title: string;
  description: string | null;
  status: "pending" | "completed" | "skipped" | "flagged";
  notes: string | null;
  requires_superadmin: boolean;
  blocks_on_step: number | null;
  kb_reference: string | null;
  tool_link: string | null;
  completed_at: string | null;
  completed_by_name: string | null;
};
type RunDetail = Run & { checklist_description: string | null; estimated_minutes: number; steps: Step[] };
type KbDoc = { doc_code: string; title: string; full_text: string | null };

const isDone = (s: Pick<Step, "status">) => s.status === "completed" || s.status === "skipped";

/** A step's in-app tool opens for the run's client: "/ads-review" → "/ads-review?client_id=15". */
function toolLink(link: string, clientId: number | null): string {
  if (!clientId || /[?&]client_id=/.test(link)) return link;
  return `${link}${link.includes("?") ? "&" : "?"}client_id=${clientId}`;
}

function kbCodes(refs: Procedure["kb_references"]): string[] {
  if (!refs) return [];
  if (Array.isArray(refs)) return refs.map(String);
  try { const parsed = JSON.parse(refs); return Array.isArray(parsed) ? parsed.map(String) : []; } catch { return []; }
}

/** "45 min", "2 hours", "1.5 hours". */
function duration(minutes: number) {
  if (!minutes) return "";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round((minutes / 60) * 10) / 10;
  return `${hours} ${hours === 1 ? "hour" : "hours"}`;
}

function Progress({ done, total, label }: { done: number; total: number; label?: string }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="sop-progress">
      <div className="bar-track" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done} aria-label={label || "Checklist progress"}>
        <span style={{ width: `${pct}%` }} />
      </div>
      <span>{done} of {total} {total === 1 ? "step" : "steps"}</span>
    </div>
  );
}

/** "KB-029 §7" → "KB-029": the document a reference points into. */
const kbBase = (code: string) => (/^KB-\d+[A-Z]?\b/i.exec(code.trim())?.[0] || code.trim().split(/\s+/)[0]).toUpperCase();
/** Knowledge base titles by code, from the list the Knowledge base page reads. Empty until it loads (or when it can't), so codes show instead. */
const KbTitles = createContext<Record<string, string>>({});

/**
 * A knowledge base reference that opens the document beside the checklist, not
 * on another page. It reads as the document's title ("Client Onboarding
 * Framework"), the code kept in the tooltip; a section ("§7") stays after it.
 */
function KbButton({ code, onOpen }: { code: string; onOpen: (code: string) => void }) {
  const title = useContext(KbTitles)[kbBase(code)];
  const section = code.trim().slice(kbBase(code).length).trim();
  return (
    <button type="button" className="sop-kb" onClick={() => onOpen(code)} aria-haspopup="dialog" title={title ? `${code}: ${title}` : undefined}>
      <BookOpen aria-hidden="true" /><span className="sop-kb-text">{title ? `${title}${section ? ` ${section}` : ""}` : code}</span>
    </button>
  );
}

/**
 * SOPs for the client named in the sidebar. The open checklist (`run`) and the
 * client (`client_id`) both live in the URL, so a copied link opens the same
 * run under the same client; opening or closing a run touches only `run`.
 */
export function SopsPage() {
  const [params, setParams] = useSearchParams();
  const runId = params.get("run");
  const [reading, setReading] = useState<string | null>(null);
  const setRun = (id: number | null) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (id === null) next.delete("run");
      else next.set("run", String(id));
      return next;
    });
  const kbList = useApi<{ doc_code: string; title: string }[]>("/api/kb");
  const kbTitles = useMemo(() => Object.fromEntries((kbList.data || []).filter((d) => d.title).map((d) => [d.doc_code.toUpperCase(), d.title])), [kbList.data]);
  return (
    <KbTitles.Provider value={kbTitles}>
      {runId
        ? <SopRun key={runId} id={runId} onBack={() => setRun(null)} openKb={setReading} />
        : <SopIndex openRun={setRun} openKb={setReading} />}
      {reading && <KbDrawer key={reading} code={reading} onClose={() => setReading(null)} />}
    </KbTitles.Provider>
  );
}

function SopIndex({ openRun, openKb }: { openRun: (id: number) => void; openKb: (code: string) => void }) {
  const { clientId } = useClientSelection();
  const templates = useApi<Procedure[]>("/api/sop");
  const runs = useApi<Run[]>(clientId ? `/api/sop/completions?client_id=${clientId}` : null);
  const [tab, setTab] = useState<"in_progress" | "completed">("in_progress");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<number | null>(null);

  // This client's runs only, even from a server that ignores ?client_id=.
  const mine = useMemo(() => (runs.data || []).filter((r) => r.client_id === clientId), [runs.data, clientId]);
  // A procedure already under way for this client is continued, not restarted.
  const openFor = useMemo(() => {
    const m = new Map<number, Run>();
    for (const r of mine) if (r.status === "in_progress" && !m.has(r.checklist_id)) m.set(r.checklist_id, r);
    return m;
  }, [mine]);

  async function start(id: number) {
    setBusy(id);
    setError("");
    try {
      const result = await api<{ completion_id: number }>(`/api/sop/${id}/start`, {
        method: "POST",
        body: JSON.stringify({ client_id: clientId }),
      });
      openRun(result.completion_id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start this checklist");
    } finally {
      setBusy(null);
    }
  }

  const ready = (templates.data || []).filter((t) => t.step_count > 0);
  const unwritten = (templates.data || []).filter((t) => !t.step_count);
  const shown = mine.filter((r) => (tab === "completed" ? r.status === "completed" : r.status === "in_progress"));

  return (
    <>
      <PageHeader title="SOPs" description="The procedures the team follows for this client, with their reference documents." />
      {!clientId ? <NoClient /> : <>
      {error && <p role="alert" className="inline-notice danger">{error}</p>}

      <section className="panel sop-panel">
        <div className="panel-heading"><h2>Procedures</h2></div>
        {templates.error ? <ErrorState message={templates.error} onRetry={templates.reload} />
          : templates.loading && !templates.data ? <LoadingState label="Loading procedures" />
          : !templates.data?.length ? <EmptyState title="No SOPs yet" description="Published operating procedures will appear here as checklists." />
          : (
            <>
              {ready.length > 0 && (
                <ul className="sop-procedures">
                  {ready.map((t) => {
                    const existing = openFor.get(t.id);
                    return (
                      <li key={t.id}>
                        <div className="sop-procedure-main">
                          <h3>{t.title}</h3>
                          {t.description && <p>{t.description}</p>}
                          <p className="sop-meta">
                            <span>{t.step_count} {t.step_count === 1 ? "step" : "steps"}</span>
                            {t.estimated_minutes > 0 && <span>About {duration(t.estimated_minutes)}</span>}
                            {kbCodes(t.kb_references).map((code) => <KbButton key={code} code={code} onOpen={openKb} />)}
                          </p>
                        </div>
                        {existing ? (
                          <div className="sop-procedure-action">
                            <Progress done={existing.done_count} total={existing.step_count} />
                            <button className="button" onClick={() => openRun(existing.id)}>Continue</button>
                          </div>
                        ) : (
                          <button className="button secondary" disabled={busy !== null} onClick={() => void start(t.id)}>
                            {busy === t.id ? "Starting…" : "Start"}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {unwritten.length > 0 && (
                // Procedures whose steps nobody has written yet: listed once, quietly, with nothing to press.
                <div className="sop-unwritten">
                  <h3>Not written yet</h3>
                  <p>These procedures have no steps yet, so they can't be started.</p>
                  <ul>
                    {unwritten.map((t) => (
                      <li key={t.id}>
                        <span>{t.title}</span>
                        {kbCodes(t.kb_references).length > 0 && (
                          <span className="sop-meta">{kbCodes(t.kb_references).map((code) => <KbButton key={code} code={code} onOpen={openKb} />)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
      </section>

      <section className="panel sop-panel">
        <div className="panel-heading sop-runs-head">
          <h2>Checklist runs</h2>
          <div className="pl-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === "in_progress"} className={tab === "in_progress" ? "active" : ""} onClick={() => setTab("in_progress")}>In progress</button>
            <button type="button" role="tab" aria-selected={tab === "completed"} className={tab === "completed" ? "active" : ""} onClick={() => setTab("completed")}>Completed</button>
          </div>
        </div>
        {runs.error ? <ErrorState message={runs.error} onRetry={runs.reload} />
          : runs.loading && !runs.data ? <LoadingState label="Loading runs" />
          : !shown.length ? <p className="muted sop-empty">{tab === "completed" ? "No finished checklists yet." : "Nothing in progress. Start a procedure above."}</p>
          : (
            <ul className="sop-runs">
              {shown.map((r) => (
                <li key={r.id}>
                  <button type="button" className="sop-run-row" onClick={() => openRun(r.id)}>
                    <span className="sop-run-title">
                      <strong>{r.checklist_title}</strong>
                      <span>
                        {r.status === "completed" && r.completed_at ? `Finished ${formatDate(r.completed_at)}` : `Started ${formatDate(r.started_at)}`}
                        {r.exec_name ? ` by ${r.exec_name}` : ""}
                      </span>
                    </span>
                    <Progress done={r.done_count} total={r.step_count} />
                  </button>
                </li>
              ))}
            </ul>
          )}
      </section>
      </>}
    </>
  );
}

function SopRun({ id, onBack, openKb }: { id: string; onBack: () => void; openKb: (code: string) => void }) {
  const request = useApi<RunDetail>(`/api/sop/completions/${id}`);
  const { user } = useAuth();
  const { clientId, setClientId } = useClientSelection();
  const isAdmin = user?.role === "superadmin";
  const [error, setError] = useState("");
  const [saving, setSaving] = useState<number | null>(null);
  const [noteOpen, setNoteOpen] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<Record<number, string>>({});

  const run = request.data;
  // The run names its client. A link copied under another client (or with no
  // client at all) moves the sidebar to the run's client instead of showing it
  // under the wrong name, and keeps the run open (a client switch otherwise
  // closes it, which landed these links on that client's SOP list).
  const owner = run?.client_id ?? null;
  useEffect(() => {
    if (owner && owner !== clientId) setClientId(owner, ["run"]);
    // Only when the run (or its client) changes; switching clients by hand is not undone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner]);
  // Switching the sidebar to another client leaves this client's run for that client's SOPs.
  const shownFor = useRef(clientId);
  useEffect(() => {
    const before = shownFor.current;
    shownFor.current = clientId;
    if (owner && before === owner && clientId !== owner) onBack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const steps = run?.steps || [];
  const byNumber = new Map(steps.map((s) => [s.step_number, s]));
  const waitingOn = (s: Step) => {
    if (!s.blocks_on_step) return null;
    const blocker = byNumber.get(s.blocks_on_step);
    return blocker && !isDone(blocker) ? blocker : null;
  };
  const done = steps.filter(isDone).length;
  const current = steps.find((s) => !isDone(s) && !waitingOn(s));

  async function save(step: Step, status: Step["status"], notes = drafts[step.item_id] ?? step.notes ?? "") {
    setSaving(step.item_id);
    setError("");
    const before = request.data;
    // Tick first, then save: the box answers the click at once, and goes back if the save fails.
    request.setData((d) => d && { ...d, steps: d.steps.map((s) => (s.item_id === step.item_id ? { ...s, status, notes } : s)) });
    try {
      await api(`/api/sop/completions/${id}/steps/${step.item_id}`, { method: "PUT", body: JSON.stringify({ status, notes }) });
      request.reload();
      return true;
    } catch (e) {
      request.setData(() => before);
      setError(e instanceof Error ? e.message : "Could not update this step");
      return false;
    } finally {
      setSaving(null);
    }
  }

  async function waive(step: Step) {
    const reason = (drafts[step.item_id] ?? step.notes ?? "").trim();
    if (!reason) {
      setNoteOpen(step.item_id);
      setError(`Write why step ${step.step_number} is being waived in its note, then waive it again.`);
      return;
    }
    await save(step, "skipped", reason);
  }

  if (request.loading && !run) return <LoadingState label="Loading checklist" />;
  if (request.error || !run) {
    return (
      <>
        <button type="button" className="back-link sop-back" onClick={onBack}><ArrowLeft aria-hidden="true" /> All SOPs</button>
        <ErrorState message={request.error || "Checklist not found"} onRetry={request.reload} />
      </>
    );
  }

  const finished = steps.length > 0 && done === steps.length;

  return (
    <>
      <button type="button" className="back-link sop-back" onClick={onBack}><ArrowLeft aria-hidden="true" /> All SOPs</button>
      <PageHeader title={run.checklist_title} description={run.checklist_description || undefined} />
      <section className="panel sop-run">
        <div className="sop-run-summary">
          <Progress done={done} total={steps.length} label={`${run.checklist_title} progress`} />
          <dl className="sop-run-facts">
            <div><dt>Client</dt><dd>{run.client_name || "Agency"}</dd></div>
            <div><dt>Started</dt><dd>{formatDate(run.started_at)}</dd></div>
            {run.estimated_minutes > 0 && <div><dt>Takes about</dt><dd>{duration(run.estimated_minutes)}</dd></div>}
          </dl>
        </div>
        {finished && (
          <p className="inline-notice sop-finished" role="status"><Check aria-hidden="true" /> Checklist complete{run.completed_at ? ` on ${formatDate(run.completed_at)}` : ""}.</p>
        )}
        {error && <p role="alert" className="inline-notice danger">{error}</p>}

        <ol className="sop-steps">
          {steps.map((s) => {
            const blocker = waitingOn(s);
            const needsAdmin = s.requires_superadmin && !isAdmin;
            const locked = !isDone(s) && (Boolean(blocker) || needsAdmin);
            const checked = isDone(s);
            const note = drafts[s.item_id] ?? s.notes ?? "";
            return (
              <li key={s.item_id} className={`sop-step${checked ? " done" : ""}${current?.item_id === s.item_id ? " current" : ""}${locked ? " locked" : ""}`}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={checked}
                  aria-label={`Step ${s.step_number}: ${s.title}`}
                  className="sop-check"
                  disabled={saving !== null || locked}
                  title={blocker ? `Finish step ${blocker.step_number} first` : needsAdmin ? "An administrator completes this step" : undefined}
                  onClick={() => void save(s, checked ? "pending" : "completed")}
                >
                  {checked ? <Check aria-hidden="true" /> : locked ? <Lock aria-hidden="true" /> : null}
                </button>
                <div className="sop-step-body">
                  <h3><span className="sop-step-num">{s.step_number}.</span> {s.title}</h3>
                  {s.description && <p className="sop-step-desc">{s.description}</p>}
                  <p className="sop-meta">
                    {s.status === "skipped" && <span className="sop-waived">Waived</span>}
                    {checked && s.completed_by_name && <span>Done by {s.completed_by_name}{s.completed_at ? ` on ${formatDate(s.completed_at)}` : ""}</span>}
                    {blocker && <span className="sop-wait"><Lock aria-hidden="true" />After step {blocker.step_number}</span>}
                    {s.requires_superadmin && <span className="sop-admin"><ShieldCheck aria-hidden="true" />Administrator step</span>}
                    {s.kb_reference && <KbButton code={s.kb_reference} onOpen={openKb} />}
                    {s.tool_link && (
                      /^https?:\/\//.test(s.tool_link)
                        ? <a className="sop-kb" href={s.tool_link} target="_blank" rel="noreferrer"><ExternalLink aria-hidden="true" />Open tool</a>
                        : <Link className="sop-kb" to={toolLink(s.tool_link, owner)}><ExternalLink aria-hidden="true" />Open tool</Link>
                    )}
                    <button type="button" className="text-button sop-note-toggle" onClick={() => setNoteOpen(noteOpen === s.item_id ? null : s.item_id)} aria-expanded={noteOpen === s.item_id}>
                      <MessageSquare aria-hidden="true" />{s.notes ? "Note" : "Add note"}
                    </button>
                  </p>
                  {s.notes && noteOpen !== s.item_id && <p className="sop-note">{s.notes}</p>}
                  {noteOpen === s.item_id && (
                    <div className="sop-note-edit">
                      <textarea
                        value={note}
                        onChange={(e) => setDrafts({ ...drafts, [s.item_id]: e.target.value })}
                        placeholder="Evidence, links, or what was found"
                        aria-label={`Note for step ${s.step_number}`}
                        rows={3}
                      />
                      <div className="operation-actions">
                        <button type="button" className="button secondary" disabled={saving !== null} onClick={async () => { if (await save(s, s.status === "flagged" ? "pending" : s.status, note)) setNoteOpen(null); }}>
                          Save note
                        </button>
                        {isAdmin && !checked && (
                          <button type="button" className="text-button" disabled={saving !== null} onClick={() => void waive(s)}>Waive step with this reason</button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </section>
    </>
  );
}

/**
 * A knowledge base document in a panel beside the checklist, so reading the
 * reference never leaves the client or the run. "Open in Knowledge base" is
 * there for the full page.
 */
function KbDrawer({ code, onClose }: { code: string; onClose: () => void }) {
  // A reference to a section ("KB-029 §7") opens its document.
  const doc = useApi<KbDoc>(`/api/kb/${encodeURIComponent(kbBase(code))}`);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = dialog.current;
    const previous = document.activeElement as HTMLElement | null;
    if (el && !el.open) el.showModal();
    el?.querySelector<HTMLElement>(".page-help-close")?.focus();
    return () => {
      if (el?.open) el.close();
      if (previous && document.contains(previous)) previous.focus();
    };
  }, []);
  // Word pastes become headings and lists, as on the Knowledge base page; the cover lines repeat the title, so they go.
  const body = useMemo(() => {
    const text = doc.data?.full_text?.trim();
    return text ? splitCover(calmHeadings(plainTextToMarkdown(text))).body : "";
  }, [doc.data?.full_text]);
  return (
    <dialog
      ref={dialog}
      className="page-help-panel sop-kb-panel"
      aria-label={doc.data?.title ? `${code}: ${doc.data.title}` : code}
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className="page-help-sheet">
        <header className="page-help-head">
          <div>
            <p className="eyebrow">{code}</p>
            <h2>{doc.data?.title || "Knowledge base"}</h2>
          </div>
          <button type="button" className="icon-button page-help-close" aria-label="Close document" onClick={onClose}>
            <X aria-hidden="true" />
          </button>
        </header>
        <div className="page-help-body">
          {doc.error ? <ErrorState title="Couldn’t open this document" message={doc.error} onRetry={doc.reload} />
            : doc.loading ? <LoadingState label="Loading document" />
            : body ? <Markdown>{body}</Markdown>
            : <p className="muted">This document has no text yet.</p>}
        </div>
        <footer className="page-help-foot">
          <Link to={`/knowledge/${encodeURIComponent(kbBase(code))}`}>Open in Knowledge base</Link>
        </footer>
      </div>
    </dialog>
  );
}
